import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import BrandMark, { logoForBrand, useBrandLogos } from '../components/BrandMark';
import BookingMedia from '../components/BookingMedia';
import BookingRescheduleDialog from '../components/BookingRescheduleDialog';
import ConfirmBar from '../components/ConfirmBar';
import HealthInspectionPanel from '../components/HealthInspectionPanel';
import { Icon, Plate, StageAge, StageBadge, vehicleName } from '../components/JobUI';
import MechanicRequests from '../components/MechanicRequests';
import QuotePanel from '../components/QuotePanel';
import Sheet from '../components/Sheet';
import CustomerPanel from '../components/CustomerPanel';
import TechnicianAssignSelect from '../components/TechnicianAssignSelect';
import VehicleReceptionPanel from '../components/VehicleReceptionPanel';
import WorkLogPanel from '../components/WorkLogPanel';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LocaleContext';
import { useServiceCatalog } from '../hooks/useServiceCatalog';
import {
  BOOKING_SELECT,
  bookingReceptionComplete,
  fetchPartsRequests,
  fetchQuoteStatuses,
  fetchTechnicians,
  markBookingHandedOver,
  skipBookingReception,
  subscribeToBookings,
  updateBookingStatus,
} from '../lib/bookingData';
import { normalizeBookingMedia } from '../lib/bookingMedia';
import { bookingStatuses, displayName, formatWhen, money, statusLabel, staySummary } from '../lib/format';
import {
  journeyStageRank,
  journeySteps,
  nextJourneyStage,
  updateBookingJourneyStage,
} from '../lib/journey';
import { describeJob, JOB_STAGES, STAGE_ICONS, stageIndex } from '../lib/jobStage';
import { isResaleBooking } from '../lib/resaleDesk';
import { supabase } from '../lib/supabase';

// All columns: newer ones (e.g. reception_skipped_at) appear once their migration is applied.
const JOB_CARD_COLUMNS = '*';

const TABS = ['checkin', 'inspection', 'work', 'quote'];

async function loadJobCard(bookingId, cardId) {
  const query = supabase.from('job_cards').select(JOB_CARD_COLUMNS);
  const { data } = cardId
    ? await query.eq('id', cardId).maybeSingle()
    : await query.eq('booking_id', bookingId).order('created_at', { ascending: false }).limit(1).maybeSingle();
  return data || null;
}

export default function BookingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isManager, profile } = useAuth();
  const t = useT();
  const { serviceLabel } = useServiceCatalog();
  const makes = useBrandLogos();

  const [row, setRow] = useState(null);
  const [jobCard, setJobCard] = useState(null);
  const [receptionComplete, setReceptionComplete] = useState(false);
  const [healthFinalized, setHealthFinalized] = useState(false);
  const [quoteStatus, setQuoteStatus] = useState(null);
  const [mechanicName, setMechanicName] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(searchParams.get('view') === 'reception' ? 'checkin' : null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [handingOver, setHandingOver] = useState(false);
  const [handoverResult, setHandoverResult] = useState(null);
  const [skipReason, setSkipReason] = useState('');
  const [workLogVersion, setWorkLogVersion] = useState(0);
  const [partsRequests, setPartsRequests] = useState([]);
  const [qcRequests, setQcRequests] = useState([]);
  const [customerFileOpen, setCustomerFileOpen] = useState(false);
  const tabsRef = useRef(null);
  const requestsRef = useRef(null);
  const assignRef = useRef(null);

  const refresh = useCallback(async () => {
    const [{ data, error: queryError }, quotes, healthReady, receptionReady, cardId] = await Promise.all([
      supabase.from('bookings').select(BOOKING_SELECT).eq('id', id).maybeSingle(),
      fetchQuoteStatuses([id]).catch(() => new Map()),
      supabase
        .rpc('booking_has_finalized_health', { p_booking_id: id })
        .then(({ data: ready }) => !!ready),
      supabase
        .rpc('booking_has_completed_reception', { p_booking_id: id })
        .then(({ data: ready }) => !!ready)
        .catch(() => null),
      supabase
        .rpc('ensure_job_card_for_booking_id', { p_booking_id: id })
        .then(({ data: ensured }) => ensured)
        .catch(() => null),
    ]);
    if (queryError) {
      setError(queryError.message);
      return null;
    }
    if (data && isResaleBooking(data)) {
      navigate(`/resale-inspections/booking/${data.id}`, { replace: true });
      return null;
    }
    const card = await loadJobCard(id, cardId);
    const [parts, qc] = card?.id
      ? await Promise.all([
          fetchPartsRequests(card.id).catch(() => []),
          supabase
            .from('technician_qc_requests')
            .select('*')
            .eq('job_card_id', card.id)
            .order('requested_at', { ascending: false })
            .then(({ data: qcRows }) => qcRows || []),
        ])
      : [[], []];
    setPartsRequests(parts);
    setQcRequests(qc);
    setRow(data);
    setJobCard(card);
    setReceptionComplete(receptionReady === true || !!card?.reception_completed_at || bookingReceptionComplete(data));
    setHealthFinalized(healthReady);
    setQuoteStatus(quotes.get(id) || null);
    return data;
  }, [id, navigate]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Stay in sync when someone else moves this job (another desk, the mechanic's app).
  useEffect(() => {
    let debounce = null;
    const unsubscribe = subscribeToBookings(() => {
      clearTimeout(debounce);
      debounce = setTimeout(() => refresh(), 400);
    }, id);
    return () => {
      clearTimeout(debounce);
      unsubscribe();
    };
  }, [id, refresh]);

  useEffect(() => {
    const techId = jobCard?.assigned_technician_id;
    if (!techId) {
      setMechanicName('');
      return undefined;
    }
    let alive = true;
    fetchTechnicians()
      .then((techs) => {
        const match = techs.find((tech) => tech.id === techId);
        if (alive) setMechanicName(match ? displayName(match) : '');
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [jobCard?.assigned_technician_id]);

  function openTab(next) {
    setTab(next);
    requestAnimationFrame(() => {
      tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function onAssigned(technicianId, cardId) {
    setJobCard((current) => ({
      ...(current || {}),
      id: cardId,
      assigned_technician_id: technicianId || null,
      status: current?.status || 'draft',
    }));
  }

  async function changeStatus(next) {
    if (!row || next === row.status) return;
    if (next === 'in_progress' && !isManager) {
      if (!receptionComplete) {
        setError(t('visit.needReception'));
        openTab('checkin');
        return;
      }
      if (!healthFinalized) {
        setError(t('visit.needHealth'));
        openTab('inspection');
        return;
      }
    }
    setSaving(true);
    setError('');
    const { error: saveError, data } = await updateBookingStatus(row.id, next);
    setSaving(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    setRow((current) => ({ ...current, ...data }));
    setConfirmCancel(false);
  }

  async function advanceJourney(stage) {
    if (!row || !stage) return;
    if (
      (stage === 'repair_in_progress' || stage === 'spare_parts_requested') &&
      !receptionComplete &&
      !isManager
    ) {
      setError(t('visit.needReception'));
      return;
    }
    if (stage === 'repair_in_progress' && row.status === 'checked_in' && !healthFinalized && !isManager) {
      setError(t('visit.needHealth'));
      openTab('inspection');
      return;
    }
    setSaving(true);
    setError('');
    const { error: saveError, data } = await updateBookingJourneyStage(row.id, stage);
    setSaving(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    setRow((current) => ({ ...current, ...data }));
  }

  async function skipCheckIn() {
    setSaving(true);
    setError('');
    const { error: skipError } = await skipBookingReception(row.id, skipReason);
    setSaving(false);
    if (skipError) {
      setError(skipError.message);
      return;
    }
    setSkipping(false);
    setSkipReason('');
    await refresh();
  }

  async function handOver(paid) {
    setSaving(true);
    setError('');
    const { error: saveError, data } = await markBookingHandedOver(row.id, paid);
    setSaving(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    setHandingOver(false);
    setHandoverResult(data || {});
    await refresh();
  }

  function perform(act) {
    if (!act || saving) return;
    switch (act.kind) {
      case 'status':
        return changeStatus(act.target);
      case 'journey':
        return advanceJourney(act.target);
      case 'panel':
        if (act.target === 'requests') {
          requestsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return undefined;
        }
        return openTab(act.target);
      case 'assign':
        assignRef.current?.querySelector('select')?.focus();
        return undefined;
      case 'handover':
        setHandingOver(true);
        return undefined;
      case 'skip_checkin':
        setSkipping(true);
        return undefined;
      default:
        return undefined;
    }
  }

  if (error && !row) return <p className="form-error">{error}</p>;
  if (!row) return <p className="empty">{t('common.loading')}</p>;

  const handedOver = jobCard?.status === 'delivered';
  const job = describeJob(row, {
    role: profile?.role,
    userId: profile?.id,
    receptionComplete,
    healthFinalized,
    quoteStatus,
    handedOver,
    assignedTechnicianId: jobCard?.assigned_technician_id || null,
    requests: {
      parts: partsRequests.filter((item) => item.status === 'pending').length,
      qc: qcRequests.filter((item) => item.status === 'pending').length,
    },
  });
  const next = job.next;
  const closed = row.status === 'completed' || row.status === 'cancelled';
  const atWorkshop = ['checked_in', 'in_progress', 'completed'].includes(row.status) || receptionComplete;
  const frontDesk = profile?.role === 'manager' || profile?.role === 'advisor';
  const phone = row.profiles?.phone;
  const vehicle = row.vehicles;
  const preferred = displayName(row.mechanic);
  const hasMedia = normalizeBookingMedia(row.media).length > 0;
  const activeTab = tab || defaultTab(job.stage);

  return (
    <section className="workspace">
      <Link to="/bookings" className="back">
        <Icon name="arrow_back" className="rtl-flip" />
        {t('job.back')}
      </Link>

      <article className={`ws-head stage-${job.stage}`}>
        <div className="ws-title">
          <BrandMark name={vehicle?.brand} src={logoForBrand(makes, vehicle?.brand)} />
          <div>
            <h1>{vehicleName(vehicle, t)}</h1>
            <p className="ws-sub">
              <Plate value={vehicle?.plate_number} />
              {vehicle?.mileage ? <span>{t('job.km', { n: Number(vehicle.mileage).toLocaleString() })}</span> : null}
              {vehicle?.vin ? <span className="mono">{t('job.vin')} {vehicle.vin}</span> : null}
            </p>
          </div>
          <span className="ws-stage">
            <StageBadge stage={job.stage} size="lg" />
            <StageAge job={job} />
          </span>
        </div>

        <dl className="ws-facts">
          <div>
            <dt>
              <Icon name="build" />
              {t('job.service')}
            </dt>
            <dd>{serviceLabel(row.service_type, 'common.booking')}</dd>
          </div>
          <div>
            <dt>
              <Icon name="schedule" />
              {t('job.appointment')}
            </dt>
            <dd className={job.attention.includes('late') ? 'is-late' : ''}>
              {formatWhen(row.scheduled_at || row.created_at)}
              {row.branches?.name ? <small>{row.branches.name}</small> : null}
            </dd>
          </div>
          <div>
            <dt>
              <Icon name="person" />
              {t('job.customer')}
            </dt>
            <dd>
              <Link to={`/customers?open=${row.customer_id}`}>{displayName(row.profiles)}</Link>
              {phone ? (
                <a className="ws-call" href={`tel:${phone}`}>
                  <Icon name="call" />
                  {phone}
                </a>
              ) : null}
              {row.customer_id ? (
                <button
                  type="button"
                  className="ghost ws-customer-file"
                  onClick={() => setCustomerFileOpen(true)}
                >
                  <Icon name="folder_shared" />
                  {t('job.customerFile')}
                </button>
              ) : null}
            </dd>
          </div>
          <div>
            <dt>
              <Icon name="engineering" />
              {t('job.mechanic')}
            </dt>
            <dd className={job.assignedTechnicianId ? '' : 'is-missing'}>
              {job.assignedTechnicianId ? mechanicName || '—' : t('assign.unassigned')}
              {preferred !== '—' ? <small>{t('job.preferred', { name: preferred })}</small> : null}
            </dd>
          </div>
        </dl>

        <div className={`ws-notes${row.problem_description ? '' : ' empty'}`}>
          <Icon name="sticky_note_2" />
          <div>
            <strong>{t('job.notes')}</strong>
            <p>{row.problem_description || t('job.noNotes')}</p>
            {row.pickup_notes ? (
              <p>
                <em>{t('job.pickupNotes')}:</em> {row.pickup_notes}
              </p>
            ) : null}
          </div>
        </div>

        <StageStepper row={row} stage={job.stage} />
      </article>

      {error ? <p className="form-error">{error}</p> : null}

      <div className="ws-grid">
        <div className="ws-main">
          <NextStepCard
            job={job}
            next={next}
            saving={saving}
            onAct={perform}
            handedOver={handedOver}
            cancelled={row.status === 'cancelled'}
            assignSlot={
              next?.kind === 'assign' ? (
                <div ref={assignRef} className="ws-assign-inline">
                  <TechnicianAssignSelect
                    bookingId={row.id}
                    jobCardId={jobCard?.id}
                    assignedTechnicianId={jobCard?.assigned_technician_id}
                    branchId={row.branch_id}
                    onAssigned={onAssigned}
                  />
                </div>
              ) : null
            }
          />

          {handingOver ? (
            <section className="skip-box" aria-live="polite">
              <h3>{t('job.handoverTitle')}</h3>
              <p>{t('job.handoverHint')}</p>
              <div className="row-actions">
                <button type="button" className="ghost" onClick={() => setHandingOver(false)} disabled={saving}>
                  {t('common.cancel')}
                </button>
                <button type="button" className="ghost" onClick={() => handOver(false)} disabled={saving}>
                  {t('job.handoverUnpaid')}
                </button>
                <button type="button" className="primary" onClick={() => handOver(true)} disabled={saving}>
                  {saving ? t('common.working') : t('job.handoverPaid')}
                </button>
              </div>
            </section>
          ) : null}

          {handoverResult ? (
            <p className="form-ok">
              {handoverResult.invoice_id
                ? t('job.invoiceIssued', {
                    status: handoverResult.invoice_status === 'paid' ? t('job.invoicePaid') : t('job.invoiceUnpaid'),
                    amount: money(handoverResult.total_amount, 2),
                  })
                : t('job.noInvoice')}
            </p>
          ) : null}

          {skipping ? (
            <section className="skip-box" aria-live="polite">
              <h3>{t('job.skipTitle')}</h3>
              <p>{t('job.skipConfirm')}</p>
              <label>
                {t('job.skipReason')}
                <input value={skipReason} onChange={(event) => setSkipReason(event.target.value)} />
              </label>
              <div className="row-actions">
                <button type="button" className="ghost" onClick={() => setSkipping(false)} disabled={saving}>
                  {t('job.keep')}
                </button>
                <button type="button" className="primary" onClick={skipCheckIn} disabled={saving}>
                  {saving ? t('common.working') : t('job.skipDo')}
                </button>
              </div>
            </section>
          ) : null}

          <MechanicRequests
            ref={requestsRef}
            parts={partsRequests}
            qc={qcRequests}
            canReview={frontDesk}
            userId={profile?.id}
            onChanged={refresh}
          />

          <section className="ws-tabs" ref={tabsRef}>
            <div className="ws-tablist" role="tablist">
              {TABS.map((id) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === id}
                  className={`ws-tab${activeTab === id ? ' on' : ''}${
                    next?.kind === 'panel' && next.target === id ? ' is-next' : ''
                  }`}
                  onClick={() => setTab(id)}
                >
                  <Icon name={TAB_ICONS[id]} />
                  {t(`job.tab.${id}`)}
                  {tabDone(id, { receptionComplete, healthFinalized, quoteStatus, row }) ? (
                    <Icon name="check_circle" className="ws-tab-done" />
                  ) : null}
                </button>
              ))}
            </div>
            <div className="ws-tabpanel" role="tabpanel">
              {activeTab === 'checkin' ? (
                jobCard?.reception_skipped_at ? (
                  <p className="ws-locked">
                    <Icon name="fast_forward" />
                    <span>
                      {t('job.skipped', { when: formatWhen(jobCard.reception_skipped_at) })}
                      {jobCard.reception_skip_reason ? (
                        <>
                          <br />
                          {t('job.skippedReason', { reason: jobCard.reception_skip_reason })}
                        </>
                      ) : null}
                    </span>
                  </p>
                ) : receptionComplete || (frontDesk && row.status !== 'cancelled') ? (
                  <VehicleReceptionPanel
                    booking={row}
                    onCompleted={async (card) => {
                      setReceptionComplete(true);
                      setJobCard((current) => ({ ...(current || {}), ...card }));
                      await refresh();
                    }}
                  />
                ) : (
                  <p className="ws-locked">
                    <Icon name="hourglass_top" />
                    {t('job.checkinWaiting')}
                  </p>
                )
              ) : !atWorkshop ? (
                <p className="ws-locked">
                  <Icon name="lock" />
                  {t('job.tabLocked')}
                </p>
              ) : activeTab === 'inspection' ? (
                <HealthInspectionPanel
                  booking={row}
                  onFinalized={() => {
                    setHealthFinalized(true);
                    refresh();
                  }}
                />
              ) : activeTab === 'work' ? (
                <WorkLogPanel booking={row} onPartsChanged={() => setWorkLogVersion((v) => v + 1)} />
              ) : (
                <QuotePanel booking={row} workLogVersion={workLogVersion} onSent={refresh} />
              )}
            </div>
          </section>
        </div>

        <aside className="ws-side">
          {row.customer_id ? (
            <section className="ws-card">
              <h2>{t('job.customerFile')}</h2>
              <p className="ws-plain">{t('job.customerFileHint')}</p>
              <button
                type="button"
                className="primary"
                onClick={() => setCustomerFileOpen(true)}
              >
                <Icon name="folder_shared" />
                {t('job.customerFileOpen')}
              </button>
            </section>
          ) : null}

          <section className="ws-card">
            <h2>{t('job.mechanic')}</h2>
            {frontDesk && next?.kind !== 'assign' ? (
              <TechnicianAssignSelect
                bookingId={row.id}
                jobCardId={jobCard?.id}
                assignedTechnicianId={jobCard?.assigned_technician_id}
                branchId={row.branch_id}
                onAssigned={onAssigned}
              />
            ) : (
              <p className="ws-plain">{mechanicName || t('assign.unassigned')}</p>
            )}
          </section>

          <section className="ws-card">
            <h2>{t('job.appointment')}</h2>
            <ul className="ws-kv">
              <li>
                <span>{t('job.branch')}</span>
                <strong>
                  {row.branches?.name || '—'}
                  {row.branches?.address ? <small>{row.branches.address}</small> : null}
                </strong>
              </li>
              <li>
                <span>{t('visit.stay')}</span>
                <strong>{staySummary(row)}</strong>
              </li>
              <li>
                <span>{t('visit.opened')}</span>
                <strong>{formatWhen(row.created_at)}</strong>
              </li>
              {row.cancel_reason ? (
                <li>
                  <span>{t('visit.cancelReason')}</span>
                  <strong>{row.cancel_reason}</strong>
                </li>
              ) : null}
            </ul>
            {isManager && ['pending', 'confirmed'].includes(row.status) ? (
              <BookingRescheduleDialog
                booking={row}
                onRescheduled={(scheduledAt) => setRow((current) => ({ ...current, scheduled_at: scheduledAt }))}
              />
            ) : null}
          </section>

          {hasMedia ? (
            <section className="ws-card">
              <BookingMedia media={row.media} title={t('job.photos')} />
            </section>
          ) : null}


          {atWorkshop && row.status !== 'cancelled' ? (
            <DetailedSteps row={row} isManager={isManager} saving={saving} onPick={advanceJourney} />
          ) : null}

          {isManager && row.status !== 'cancelled' ? (
            <details className="ws-card ws-disclosure">
              <summary>
                <Icon name="admin_panel_settings" />
                {t('job.override')}
              </summary>
              <p className="ws-plain">{t('job.overrideHint')}</p>
              <select
                aria-label={t('job.override')}
                value={row.status}
                disabled={saving}
                onChange={(event) => changeStatus(event.target.value)}
              >
                {bookingStatuses.map((item) => (
                  <option key={item} value={item}>
                    {statusLabel(item)}
                  </option>
                ))}
              </select>
            </details>
          ) : null}

          {!closed ? (
            confirmCancel ? (
              <ConfirmBar
                message={t('job.cancelConfirm')}
                confirmLabel={t('job.cancel')}
                cancelLabel={t('job.keep')}
                busy={saving}
                onConfirm={() => changeStatus('cancelled')}
                onCancel={() => setConfirmCancel(false)}
              />
            ) : (
              <button type="button" className="ghost danger ws-cancel" onClick={() => setConfirmCancel(true)}>
                <Icon name="block" />
                {t('job.cancel')}
              </button>
            )
          ) : null}
        </aside>
      </div>

      {customerFileOpen && row.customer_id ? (
        <Sheet
          variant="modal"
          wide
          className="sheet-customer"
          bodyClassName="sheet-body-customer"
          title={displayName(row.profiles)}
          subtitle={t('job.customerFileSubtitle')}
          onClose={() => setCustomerFileOpen(false)}
        >
          <CustomerPanel
            id={row.customer_id}
            focusVehicleId={row.vehicle_id}
            compact
          />
        </Sheet>
      ) : null}
    </section>
  );
}

const TAB_ICONS = {
  checkin: 'key',
  inspection: 'troubleshoot',
  work: 'handyman',
  quote: 'request_quote',
};

function defaultTab(stage) {
  if (stage === 'booked') return 'checkin';
  if (stage === 'checked_in' || stage === 'inspection') return 'inspection';
  return 'work';
}

function tabDone(id, { receptionComplete, healthFinalized, quoteStatus, row }) {
  if (id === 'checkin') return receptionComplete;
  if (id === 'inspection') return healthFinalized;
  if (id === 'quote') {
    return (
      quoteStatus === 'approved' ||
      quoteStatus === 'partial' ||
      (row.status === 'completed' && Boolean(quoteStatus))
    );
  }
  if (id === 'work') return row.status === 'completed';
  return false;
}

function NextStepCard({ job, next, saving, onAct, assignSlot, handedOver, cancelled }) {
  const t = useT();
  if (!next) {
    return (
      <section className={`next-card is-idle${cancelled ? ' is-cancelled' : ''}`}>
        <Icon name={cancelled ? 'block' : 'task_alt'} className="next-card-icon" />
        <div>
          <span className="next-card-kicker">{t('job.nextStep')}</span>
          <h2>{cancelled ? t('job.cancelledNote') : handedOver ? t('job.handedOver') : t('job.nothingNext')}</h2>
        </div>
      </section>
    );
  }
  const waiting = next.kind === 'wait';
  return (
    <section className={`next-card${waiting ? ' is-waiting' : ''} stage-${job.stage}`} aria-live="polite">
      <Icon name={next.icon} className="next-card-icon" />
      <div className="next-card-body">
        <span className="next-card-kicker">{t('job.nextStep')}</span>
        <h2>{t(`next.${next.id}`)}</h2>
        <p>{t(`next.${next.id}.hint`)}</p>
        {assignSlot}
        <div className="next-card-actions">
          {!waiting && next.kind !== 'assign' ? (
            <button type="button" className="primary next-primary" disabled={saving} onClick={() => onAct(next)}>
              {saving ? t('common.working') : t(`next.${next.id}`)}
              <Icon name="arrow_forward" className="rtl-flip" />
            </button>
          ) : null}
          {next.alternatives?.length ? (
            <>
              {!waiting ? <span className="next-or">{t('job.otherOptions')}</span> : null}
              {next.alternatives.map((alt) => (
                <button
                  key={alt.id}
                  type="button"
                  className={waiting ? 'primary next-primary' : 'ghost'}
                  disabled={saving}
                  onClick={() => onAct(alt)}
                >
                  <Icon name={alt.icon} />
                  {t(`next.${alt.id}`)}
                </button>
              ))}
            </>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function stageStamp(row, stage) {
  const at = row.journey_stage_at || {};
  switch (stage) {
    case 'booked':
      return row.confirmed_at || row.created_at;
    case 'checked_in':
      return row.checked_in_at || at.vehicle_received;
    case 'inspection':
      return at.initial_inspection || at.computer_diagnosis;
    case 'in_progress':
      return row.in_progress_at || at.repair_in_progress;
    case 'ready':
      return row.completed_at || at.ready_for_collection;
    default:
      return null;
  }
}

function StageStepper({ row, stage }) {
  const t = useT();
  const listRef = useRef(null);
  const current = stageIndex(stage);
  const cancelled = stage === 'cancelled';

  // On narrow screens the stepper scrolls sideways; keep the current stage in view.
  useEffect(() => {
    const list = listRef.current;
    const now = list?.querySelector('.step-now');
    if (!list || !now || list.scrollWidth <= list.clientWidth) return;
    list.scrollLeft += now.getBoundingClientRect().left - list.getBoundingClientRect().left - list.clientWidth / 3;
  }, [stage]);

  return (
    <ol ref={listRef} className={`stepper${cancelled ? ' is-cancelled' : ''}`} aria-label={t('job.progress')}>
      {JOB_STAGES.map((id, index) => {
        const state = cancelled ? 'off' : index < current ? 'done' : index === current ? 'now' : 'todo';
        const stamp = state === 'done' || state === 'now' ? stageStamp(row, id) : null;
        return (
          <li key={id} className={`step step-${state} stage-${id}`} aria-current={state === 'now' ? 'step' : undefined}>
            <span className="step-dot">
              <Icon name={state === 'done' ? 'check' : STAGE_ICONS[id]} />
            </span>
            <span className="step-copy">
              <strong>{t(`stage.${id}`)}</strong>
              <small>
                {state === 'now'
                  ? t('job.stepNow')
                  : stamp
                    ? formatWhen(stamp)
                    : state === 'done'
                      ? t('job.stepDone')
                      : t(`stageHint.${id}`)}
              </small>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** The customer-app journey steps, for moving a job precisely. */
function DetailedSteps({ row, isManager, saving, onPick }) {
  const t = useT();
  const closed = row.status === 'completed' || row.status === 'cancelled';
  const journeyNext = nextJourneyStage(row.journey_stage);

  function canJump(stepId) {
    if (saving || closed || stepId === row.journey_stage) return false;
    if (journeyStageRank(stepId) <= journeyStageRank(row.journey_stage)) return false;
    return isManager || stepId === journeyNext;
  }

  return (
    <details className="ws-card ws-disclosure">
      <summary>
        <Icon name="format_list_numbered" />
        {t('job.detailed')}
      </summary>
      <p className="ws-plain">{t('job.detailedHint')}</p>
      <div className="journey-rail">
        {journeySteps(row).map((group) => (
          <section key={group.id} className="journey-group">
            <h3 className="journey-group-label">{group.label}</h3>
            <ol className="journey-steps">
              {group.steps.map((step) => (
                <li key={step.id} className={[step.done ? 'done' : '', step.current ? 'current' : ''].filter(Boolean).join(' ')}>
                  <button
                    type="button"
                    className="journey-step-btn"
                    disabled={!canJump(step.id)}
                    onClick={() => onPick(step.id)}
                    title={canJump(step.id) ? t('visit.mark', { name: step.title }) : step.title}
                  >
                    <span className="journey-mark" />
                    <span className="journey-copy">
                      <strong>{step.title}</strong>
                      <small>{step.when || (step.current ? t('visit.now') : '')}</small>
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </section>
        ))}
      </div>
    </details>
  );
}
